<script setup>
/**
 * @fileoverview ConsoleToolbar - Console controls and status display
 *
 * Toolbar for VM console: optional Back to lab, VM title, connection status,
 * optional Close session, lab VM menu (Switch VM) with open-session hints,
 * clipboard, Ctrl+Alt+Del, Minimize (at end next to fullscreen), and fullscreen.
 *
 * The VM menu lists every VM in the lab; rows show whether a browser console
 * session already exists for that VM. Minimize hides the overlay while the
 * WebSocket stays alive in the store.
 *
 * Back: before navigation, if this toolbar is for the active console session
 * (match `sessionKey` or lab `currentVmId`), we minimize so the fixed host
 * hides — otherwise the route would change but the overlay would still cover
 * the page.
 */

import {
  ChevronLeft,
  ChevronDown,
  Maximize,
  Minimize,
  Clipboard,
  Keyboard,
  Send,
  Layers,
  X
} from 'lucide-vue-next'
import { computed, ref } from 'vue'
import { RouterLink } from 'vue-router'
import { useConsoleSessionsStore } from '@/stores/consoleSessions'

const props = defineProps({
  /** When set, shows a Back control that navigates here (session stays open). */
  backHref: {
    type: String,
    default: ''
  },
  /** Lab id for scoping “console already open” hints in the VM menu. */
  labId: {
    type: String,
    default: ''
  },
  /** When true, shows Close session (disconnects this VM’s console in the browser). */
  showCloseSession: {
    type: Boolean,
    default: false
  },
  vmName: {
    type: String,
    required: true
  },
  connectionState: {
    type: String,
    required: true,
    validator: value =>
      ['idle', 'connecting', 'connected', 'disconnected', 'reconnecting', 'error'].includes(value)
  },
  reconnectAttempts: {
    type: Number,
    default: 0
  },
  /**
   * VMs in the current lab. Used to populate the Switch VM menu (all VMs,
   * including the one currently displayed).
   */
  availableVMs: {
    type: Array,
    default: () => []
  },
  currentVmId: {
    type: String,
    default: ''
  },
  /** Store session key (`share:…` for share portal; omit for lab = vmId). */
  sessionKey: {
    type: String,
    default: ''
  }
})

const emit = defineEmits([
  'minimize',
  'fullscreen',
  'sendClipboard',
  'sendCtrlAltDel',
  'dialogOpen',
  'dialogClose',
  'openVM',
  'closeSession'
])

const sessionsStore = useConsoleSessionsStore()

const isFullscreen = ref(false)
const showClipboardDialog = ref(false)
const clipboardText = ref('')
const showSwitchVMMenu = ref(false)

const vmListForMenu = computed(() => {
  const list = (props.availableVMs || []).filter(vm => vm && vm.id)
  return [...list].sort((a, b) => {
    if (a.id === props.currentVmId) return -1
    if (b.id === props.currentVmId) return 1
    return (a.name || a.id).localeCompare(b.name || b.id, undefined, { sensitivity: 'base' })
  })
})

const hasAnyLabVms = computed(() => vmListForMenu.value.length > 0)

function browserSessionOpenForVm(vmId) {
  for (const s of sessionsStore.sessionList) {
    if (s.vmId !== vmId) continue
    if (props.labId && s.labId !== props.labId) continue
    return true
  }
  return false
}

/** Menu row is inactive for click when viewing this VM, or stopped with no session. */
function vmMenuItemDisabled(vm) {
  if (vm.id === props.currentVmId) return true
  const running = !vm.status || vm.status === 'running'
  if (running) return false
  return !browserSessionOpenForVm(vm.id)
}

function handleMinimize() {
  emit('minimize')
}

function toolbarSessionKey() {
  return props.sessionKey || props.currentVmId
}

function onBackLinkClick() {
  const sk = toolbarSessionKey()
  if (!sk || !props.backHref) return
  const active = sessionsStore.activeSession
  if (active?.sessionKey === sk) {
    sessionsStore.minimizeSession(sk)
  }
}

function handleCloseSession() {
  emit('closeSession')
}

function toggleFullscreen() {
  isFullscreen.value = !isFullscreen.value
  emit('fullscreen', isFullscreen.value)
}

function openClipboardDialog() {
  showClipboardDialog.value = true
  clipboardText.value = ''
  emit('dialogOpen')
}

function closeClipboardDialog() {
  showClipboardDialog.value = false
  clipboardText.value = ''
  emit('dialogClose')
}

function sendClipboard() {
  if (clipboardText.value.trim()) {
    emit('sendClipboard', clipboardText.value)
    closeClipboardDialog()
  }
}

function sendCtrlAltDel() {
  emit('sendCtrlAltDel')
}

function toggleSwitchVMMenu() {
  showSwitchVMMenu.value = !showSwitchVMMenu.value
}

function handleSwitchVM(vmId) {
  emit('openVM', vmId)
  showSwitchVMMenu.value = false
}

function onSelectMenuVm(vm) {
  if (vm.id === props.currentVmId) {
    showSwitchVMMenu.value = false
    return
  }
  if (vmMenuItemDisabled(vm)) return
  handleSwitchVM(vm.id)
}

function getStateText() {
  if (props.connectionState === 'reconnecting') {
    return `Reconnecting (${props.reconnectAttempts}/5)`
  }
  const states = {
    idle: 'Initializing',
    connecting: 'Connecting',
    connected: 'Connected',
    disconnected: 'Disconnected',
    error: 'Connection Error'
  }
  return states[props.connectionState] || 'Unknown'
}

function getStateBadgeClasses() {
  const classes = {
    idle: 'bg-gray-100 text-gray-700 border-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-700',
    connecting:
      'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-900/10 dark:text-amber-400 dark:border-amber-800',
    reconnecting:
      'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-900/10 dark:text-amber-400 dark:border-amber-800 animate-pulse',
    connected:
      'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-900/10 dark:text-emerald-400 dark:border-emerald-800',
    disconnected:
      'bg-gray-100 text-gray-700 border-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-700',
    error:
      'bg-red-50 text-red-700 border-red-200 dark:bg-red-900/10 dark:text-red-400 dark:border-red-800'
  }
  return classes[props.connectionState] || classes.idle
}

function isControlsDisabled() {
  return props.connectionState !== 'connected'
}
</script>

<template>
  <div
    class="console-toolbar bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700"
  >
    <div
      class="flex w-full min-w-0 flex-wrap items-center gap-x-2 gap-y-2 px-4 py-3 sm:flex-nowrap"
    >
      <div
        v-if="backHref"
        class="flex min-w-0 shrink-0 items-center gap-1.5 sm:gap-2"
      >
        <RouterLink
          :to="backHref"
          class="inline-flex min-h-[44px] min-w-[44px] items-center justify-center gap-2 rounded-lg px-3 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:text-gray-300 dark:hover:bg-gray-700 sm:min-w-0"
          aria-label="Back to lab"
          title="Return to lab (console session stays open in the background)"
          @click="onBackLinkClick"
        >
          <ChevronLeft class="h-5 w-5 sm:h-4 sm:w-4" />
          <span class="hidden sm:inline">Back</span>
        </RouterLink>
      </div>

      <div class="min-w-0 flex-1">
        <h2 class="truncate text-base font-semibold text-gray-900 dark:text-white sm:text-lg">
          {{ vmName }}
        </h2>
      </div>

      <div class="ml-auto flex shrink-0 flex-wrap items-center gap-1.5 sm:gap-2">
        <span
          :class="[
            'inline-flex shrink-0 items-center rounded-full border px-2.5 py-1 text-xs font-medium sm:px-3',
            getStateBadgeClasses()
          ]"
          role="status"
          aria-live="polite"
          :aria-label="`Console connection status: ${getStateText()}`"
        >
          <span
            v-if="connectionState === 'connected'"
            class="mr-2 h-2 w-2 animate-pulse rounded-full bg-current"
            aria-hidden="true"
          ></span>
          <span
            v-else-if="connectionState === 'connecting'"
            class="mr-2 h-2 w-2 rounded-full bg-current"
            aria-hidden="true"
          ></span>
          {{ getStateText() }}
        </span>

        <button
          v-if="showCloseSession"
          type="button"
          class="inline-flex min-h-[44px] min-w-[44px] items-center justify-center gap-2 rounded-lg px-2.5 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-red-50 hover:text-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 dark:text-gray-300 dark:hover:bg-red-950/40 dark:hover:text-red-300"
          aria-label="Close this console session"
          title="Disconnect and remove this console session from the browser"
          @click="handleCloseSession"
        >
          <X class="h-5 w-5 sm:h-4 sm:w-4" aria-hidden="true" />
          <span class="hidden lg:inline">Close session</span>
        </button>

        <div class="relative">
          <button
            type="button"
            :disabled="!hasAnyLabVms"
            class="inline-flex min-h-[44px] min-w-[44px] items-center justify-center gap-2 rounded-lg px-2.5 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:cursor-not-allowed disabled:opacity-50 dark:text-gray-300 dark:hover:bg-gray-700"
            :aria-label="
              !hasAnyLabVms
                ? 'Lab VMs (none listed)'
                : 'Open or switch to a lab VM console'
            "
            :aria-expanded="showSwitchVMMenu"
            aria-haspopup="menu"
            title="Choose a VM to open or focus its console"
            @click="toggleSwitchVMMenu"
          >
            <Layers class="h-5 w-5 sm:h-4 sm:w-4" aria-hidden="true" />
            <span class="hidden lg:inline">Switch VM</span>
            <ChevronDown
              class="hidden h-4 w-4 opacity-70 sm:inline"
              aria-hidden="true"
            />
          </button>
          <div
            v-if="showSwitchVMMenu && vmListForMenu.length > 0"
            role="menu"
            class="absolute right-0 z-50 mt-2 max-h-80 w-[min(100vw-2rem,22rem)] overflow-y-auto rounded-lg border border-gray-200 bg-white shadow-xl dark:border-gray-700 dark:bg-gray-800"
          >
            <ul class="py-1">
              <li v-for="vm in vmListForMenu" :key="vm.id">
                <button
                  type="button"
                  role="menuitem"
                  :disabled="vmMenuItemDisabled(vm)"
                  class="flex w-full items-start gap-2 px-3 py-2.5 text-left text-sm text-gray-700 hover:bg-gray-100 focus:bg-gray-100 focus:outline-none disabled:cursor-not-allowed disabled:opacity-50 dark:text-gray-200 dark:hover:bg-gray-700 dark:focus:bg-gray-700"
                  @click="onSelectMenuVm(vm)"
                >
                  <span
                    class="mt-1.5 h-2 w-2 shrink-0 rounded-full"
                    :class="
                      browserSessionOpenForVm(vm.id)
                        ? 'bg-emerald-500'
                        : 'bg-gray-300 dark:bg-gray-600'
                    "
                    :title="
                      browserSessionOpenForVm(vm.id)
                        ? 'Console session open in this browser'
                        : 'No console session for this VM'
                    "
                    aria-hidden="true"
                  />
                  <span class="min-w-0 flex-1">
                    <span class="flex flex-wrap items-center gap-x-2 gap-y-0.5">
                      <span class="font-medium">{{ vm.name || vm.id }}</span>
                      <span
                        v-if="vm.id === currentVmId"
                        class="rounded bg-indigo-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-indigo-800 dark:bg-indigo-900/40 dark:text-indigo-200"
                      >
                        Viewing
                      </span>
                    </span>
                    <span
                      v-if="vm.status && vm.status !== 'running'"
                      class="mt-0.5 block text-xs text-gray-500 dark:text-gray-400"
                    >
                      VM status: {{ vm.status }}<template v-if="browserSessionOpenForVm(vm.id)">
                        — console open
                      </template>
                    </span>
                    <span
                      v-else-if="browserSessionOpenForVm(vm.id)"
                      class="mt-0.5 block text-xs text-emerald-700 dark:text-emerald-400"
                    >
                      Console open in this browser
                    </span>
                  </span>
                </button>
              </li>
            </ul>
          </div>
        </div>

        <button
          @click="openClipboardDialog"
          :disabled="isControlsDisabled()"
          class="inline-flex min-h-[44px] min-w-[44px] items-center justify-center gap-2 rounded-lg px-2.5 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:cursor-not-allowed disabled:opacity-50 dark:text-gray-300 dark:hover:bg-gray-700"
          :aria-label="
            isControlsDisabled()
              ? 'Send clipboard to VM (disabled - not connected)'
              : 'Send clipboard to VM'
          "
          title="Send clipboard text to VM"
        >
          <Clipboard class="h-5 w-5 sm:h-4 sm:w-4" aria-hidden="true" />
          <span class="hidden lg:inline">Send Clipboard</span>
        </button>

        <button
          @click="sendCtrlAltDel"
          :disabled="isControlsDisabled()"
          class="inline-flex min-h-[44px] min-w-[44px] items-center justify-center gap-2 rounded-lg px-2.5 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:cursor-not-allowed disabled:opacity-50 dark:text-gray-300 dark:hover:bg-gray-700"
          :aria-label="
            isControlsDisabled()
              ? 'Send Ctrl+Alt+Del (disabled - not connected)'
              : 'Send Ctrl+Alt+Del to VM'
          "
          title="Send Ctrl+Alt+Del (useful for Windows VMs)"
        >
          <Keyboard class="h-5 w-5 sm:h-4 sm:w-4" aria-hidden="true" />
          <span class="hidden lg:inline">Ctrl+Alt+Del</span>
        </button>

        <button
          type="button"
          class="inline-flex min-h-[44px] min-w-[44px] items-center justify-center gap-2 rounded-lg px-2.5 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:text-gray-300 dark:hover:bg-gray-700"
          aria-label="Minimize console (session stays alive for 1 hour)"
          title="Minimize console — session stays alive for 1 hour"
          @click="handleMinimize"
        >
          <ChevronDown class="h-5 w-5 sm:h-4 sm:w-4" aria-hidden="true" />
          <span class="hidden sm:inline">Minimize</span>
        </button>

        <button
          @click="toggleFullscreen"
          class="inline-flex min-h-[44px] min-w-[44px] items-center justify-center gap-2 rounded-lg px-2.5 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:text-gray-300 dark:hover:bg-gray-700"
          :aria-label="isFullscreen ? 'Exit fullscreen mode' : 'Enter fullscreen mode'"
          :aria-pressed="isFullscreen"
        >
          <Maximize v-if="!isFullscreen" class="h-5 w-5 sm:h-4 sm:w-4" aria-hidden="true" />
          <Minimize v-else class="h-5 w-5 sm:h-4 sm:w-4" aria-hidden="true" />
          <span class="hidden md:inline">
            {{ isFullscreen ? 'Exit Fullscreen' : 'Fullscreen' }}
          </span>
        </button>
      </div>
    </div>

    <div
      v-if="showClipboardDialog"
      class="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
      @click.self="closeClipboardDialog"
    >
      <div
        class="mx-4 w-full max-w-md rounded-lg bg-white shadow-xl dark:bg-gray-800"
        role="dialog"
        aria-labelledby="clipboard-dialog-title"
        aria-modal="true"
      >
        <div
          class="flex items-center justify-between border-b border-gray-200 px-6 py-4 dark:border-gray-700"
        >
          <h3
            id="clipboard-dialog-title"
            class="text-lg font-semibold text-gray-900 dark:text-white"
          >
            Send Clipboard to VM
          </h3>
          <button
            class="rounded text-gray-400 transition-colors hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:hover:text-gray-300"
            aria-label="Close dialog"
            @click="closeClipboardDialog"
          >
            <X class="h-5 w-5" />
          </button>
        </div>

        <div class="px-6 py-4">
          <label
            for="clipboard-text"
            class="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300"
          >
            Enter text to send
          </label>
          <textarea
            id="clipboard-text"
            v-model="clipboardText"
            rows="6"
            class="w-full resize-none rounded-lg border border-gray-300 bg-white px-3 py-2 text-gray-900 placeholder-gray-400 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:border-gray-600 dark:bg-gray-900 dark:text-white"
            placeholder="Paste or type text here..."
            autofocus
            @keydown.stop
            @keyup.stop
            @keypress.stop
          ></textarea>
          <p class="mt-2 text-xs text-gray-500 dark:text-gray-400">
            This text will be sent to the VM's clipboard and can be pasted inside the VM.
          </p>
        </div>

        <div
          class="flex items-center justify-end gap-3 border-t border-gray-200 px-6 py-4 dark:border-gray-700"
        >
          <button
            class="rounded-lg px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:text-gray-300 dark:hover:bg-gray-700"
            @click="closeClipboardDialog"
          >
            Cancel
          </button>
          <button
            class="inline-flex items-center gap-2 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:cursor-not-allowed disabled:opacity-50"
            :disabled="!clipboardText.trim()"
            aria-label="Send clipboard text to virtual machine"
            @click="sendClipboard"
          >
            <Send class="h-4 w-4" aria-hidden="true" />
            Send to VM
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.console-toolbar {
  position: relative;
  z-index: 10;
}
</style>
